import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { DEFAULT_PIPELINE_FORM, PipelineFormState, pipelineToForm, PIPELINE_STAGES } from './pipelineFormShared';

const CSS = `
.pf-page { min-height: 100vh; background: #fafafa; font-family: 'DM Sans', system-ui, sans-serif; padding-bottom: 88px; }
.pf-top { position: sticky; top: 0; z-index: 20; background: #fff; border-bottom: 1px solid #e5e7eb; padding: 16px 24px; }
.pf-back { background: none; border: none; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; padding: 0; margin-bottom: 8px; display: block; }
.pf-title { margin: 0; font-size: 22px; font-weight: 800; }
.pf-body { max-width: 920px; margin: 0 auto; padding: 24px; }
.pf-section { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px 22px; margin-bottom: 16px; }
.pf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
.pf-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.pf-input, .pf-select { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; box-sizing: border-box; }
.pf-footer { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #e5e7eb; padding: 14px 24px; display: flex; justify-content: flex-end; gap: 10px; }
.pf-btn-save { padding: 11px 28px; border: none; border-radius: 8px; background: #2563eb; color: #fff; font-weight: 800; cursor: pointer; }
.pf-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-weight: 600; }
`;

export default function PipelineFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { token } = useAuth();
  const saveInFlight = useRef(false);

  const [form, setForm] = useState<PipelineFormState>({ ...DEFAULT_PIPELINE_FORM });
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof PipelineFormState>(k: K, v: PipelineFormState[K]) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setProperties((d.properties || []).filter((p: any) => !p.archived)));
  }, [token]);

  useEffect(() => {
    if (!token || !isEdit || !id) return;
    (async () => {
      setLoading(true);
      const r = await fetch(`${API_URL}/landlord/pipeline/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Not found'); setLoading(false); return; }
      setForm(pipelineToForm(d.item));
      setLoading(false);
    })();
  }, [token, isEdit, id]);

  const save = async () => {
    if (saveInFlight.current || !form.address.trim()) { setError('Address is required'); return; }
    saveInFlight.current = true;
    setSaving(true);
    setError('');
    const body = {
      ...form,
      asking_price: form.asking_price ? Number(form.asking_price) : null,
      offer_price: form.offer_price ? Number(form.offer_price) : null,
      linked_property_id: form.linked_property_id ? Number(form.linked_property_id) : null,
    };
    const url = isEdit ? `${API_URL}/landlord/pipeline/${id}` : `${API_URL}/landlord/pipeline`;
    const r = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed'); setSaving(false); saveInFlight.current = false; return; }
    showToast(isEdit ? 'Deal updated' : 'Deal added');
    navigate('/landlord/pipeline');
    setSaving(false);
    saveInFlight.current = false;
  };

  if (loading) return <div style={{ padding: 80, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;

  return (
    <div className="pf-page">
      <style>{CSS}</style>
      <div className="pf-top">
        <button type="button" className="pf-back" onClick={() => navigate('/landlord/pipeline')}>← Back to Sales & Buy</button>
        <h1 className="pf-title">{isEdit ? 'Edit Deal' : 'Add Deal'}</h1>
      </div>
      <div className="pf-body">
        {error && <div className="pf-error">{error}</div>}
        <div className="pf-section">
          <div className="pf-grid">
            <div><label className="pf-label">Address *</label><input className="pf-input" value={form.address} onChange={e => set('address', e.target.value)} /></div>
            <div><label className="pf-label">Postcode</label><input className="pf-input" value={form.postcode} onChange={e => set('postcode', e.target.value)} /></div>
            <div><label className="pf-label">Type</label>
              <select className="pf-select" value={form.pipeline_type} onChange={e => set('pipeline_type', e.target.value as 'buy' | 'sell')}>
                <option value="buy">Buying / Acquisition</option>
                <option value="sell">Selling / Disposal</option>
              </select>
            </div>
            <div><label className="pf-label">Stage</label>
              <select className="pf-select" value={form.stage} onChange={e => set('stage', e.target.value)}>
                {PIPELINE_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div><label className="pf-label">Asking Price (£)</label><input type="number" className="pf-input" value={form.asking_price} onChange={e => set('asking_price', e.target.value)} /></div>
            <div><label className="pf-label">Offer Price (£)</label><input type="number" className="pf-input" value={form.offer_price} onChange={e => set('offer_price', e.target.value)} /></div>
            <div><label className="pf-label">Target Date</label><input type="date" className="pf-input" value={form.target_date} onChange={e => set('target_date', e.target.value)} /></div>
            <div><label className="pf-label">Link to Portfolio Property</label>
              <select className="pf-select" value={form.linked_property_id} onChange={e => set('linked_property_id', e.target.value)}>
                <option value="">— None —</option>
                {properties.map((p: any) => <option key={p.id} value={p.id}>{p.address}</option>)}
              </select>
            </div>
            <div><label className="pf-label">Agent Name</label><input className="pf-input" value={form.agent_name} onChange={e => set('agent_name', e.target.value)} /></div>
            <div><label className="pf-label">Agent Phone</label><input className="pf-input" value={form.agent_phone} onChange={e => set('agent_phone', e.target.value)} /></div>
            <div><label className="pf-label">Agent Email</label><input type="email" className="pf-input" value={form.agent_email} onChange={e => set('agent_email', e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 14 }}><label className="pf-label">Notes</label>
            <textarea className="pf-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>
      </div>
      <div className="pf-footer">
        <button type="button" onClick={() => navigate('/landlord/pipeline')} style={{ padding: '11px 22px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
        <button type="button" className="pf-btn-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Deal'}</button>
      </div>
    </div>
  );
}
